/* "Add TallyPage to your home screen" – shown only on phones/tablets in a normal browser tab, never inside the installed app.
   Android/Chrome: real install prompt (beforeinstallprompt). iPhone/iPad Safari: step-by-step Share → Add to Home Screen. */
import { useEffect, useState } from "react";
import { Smartphone, Share, PlusSquare, X, Download } from "lucide-react";
import { t } from "@/i18n";

const DISMISS_KEY = "tp_install_dismissed";
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true || document.referrer.startsWith("android-app://");
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const isMobile = () => /android|iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && /macintosh/i.test(navigator.userAgent));

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);
  const [howTo, setHowTo] = useState(false);

  useEffect(() => {
    if (isStandalone() || !isMobile()) return;
    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissed && Date.now() - dismissed < 7 * 86400e3) return;
    if (isIOS()) { setIos(true); setShow(true); }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, String(Date.now())); };
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  if (!show) return null;
  const dismiss = () => { setShow(false); localStorage.setItem(DISMISS_KEY, String(Date.now())); };
  const install = async () => {
    if (!deferred) { setHowTo(true); return; }
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setShow(false);
    setDeferred(null);
  };

  return (
    <div data-testid="install-prompt" className="tp-pop fixed inset-x-3 bottom-3 z-[70] rounded-2xl bg-[#1f1533] text-white shadow-[0_16px_40px_-12px_rgba(0,0,0,0.5)] p-3.5 sm:max-w-sm sm:left-auto sm:right-4">
      <button data-testid="install-dismiss" onClick={dismiss} aria-label="Dismiss" className="absolute right-2 top-2 p-1 rounded-full text-white/60 hover:text-white hover:bg-white/10"><X className="w-4 h-4" /></button>
      <div className="flex items-start gap-3">
        <span className="w-11 h-11 rounded-xl bg-[#663399] grid place-items-center shrink-0"><Smartphone className="w-5 h-5" /></span>
        <div className="min-w-0 flex-1 pr-5">
          <div className="text-sm font-extrabold">{t("Add TallyPage to your home screen")}</div>
          <div className="text-xs text-white/70 mt-0.5">{t("Opens full-screen like an app, one tap from your phone – no app store needed.")}</div>
          {!howTo ? (
            <button data-testid="install-button" onClick={install} className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-white text-[#663399] px-3.5 py-1.5 text-xs font-extrabold active:scale-95 transition-transform"><Download className="w-3.5 h-3.5" />{ios ? t("Show me how") : t("Install app")}</button>
          ) : (
            <ol data-testid="install-howto" className="mt-2.5 space-y-1.5 text-xs text-white/85">
              {[
                <>{t("Tap the")} <Share className="w-3.5 h-3.5 inline -mt-0.5 text-sky-300" /> <b>{t("Share")}</b> {t("button at the bottom of Safari")}</>,
                <>{t("Scroll down and tap")} <PlusSquare className="w-3.5 h-3.5 inline -mt-0.5" /> <b>{t("Add to Home Screen")}</b></>,
                <>{t("Tap")} <b>{t("Add")}</b> – {t("TallyPage appears with your other apps")}</>,
              ].map((step, i) => <li key={i} className="flex items-start gap-2"><span className="w-5 h-5 shrink-0 rounded-full bg-white/15 grid place-items-center text-[10px] font-bold">{i + 1}</span><span className="flex-1 leading-5">{step}</span></li>)}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};
